// pages/HowToTeachTopicDetail.js
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { fetchWithAuth } from '../auth';
import Breadcrumbs from '../components/Breadcrumbs';
import { SkeletonCardGrid } from '../components/ui';
import { useResourceDetailModal } from '../components/ResourceDetailModalContext';

const topicTypeLabels = {
  'MISHNA': 'משנה',
  'GMARA': 'גמרא',
  'HALACHA': 'הלכה',
};

export default function HowToTeachTopicDetail() {
  const { topicType } = useParams();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const topicLabel = topicTypeLabels[topicType] || topicType;
  const { openResourceDetail } = useResourceDetailModal();

  const [categories, setCategories] = useState([]);

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const res = await fetchWithAuth(`/api/how-to-teach-toshba-topic-resources/by-topic-type/${topicType}/`);
        if (!res.ok) throw new Error('Failed to load resources');
        const data = await res.json();
        if (!ignore) {
          setCategories(data.categories || []);
          setResources(data.resources || []);
        }
      } catch {
        if (!ignore) setError('שגיאה בטעינת המשאבים');
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [topicType]);

  // Group resources by category_id; order sections by category order, then uncategorized (only sections with resources)
  const resourcesByCategory = (() => {
    const byId = new Map();
    (categories || []).forEach((c) => byId.set(c.id, { category: c, resources: [] }));
    const uncategorized = [];
    (resources || []).forEach((r) => {
      if (r.category_id != null && byId.has(r.category_id)) {
        byId.get(r.category_id).resources.push(r);
      } else {
        uncategorized.push(r);
      }
    });
    const sections = (categories || []).map((c) => ({ category: c, resources: byId.get(c.id).resources })).filter((s) => s.resources.length > 0);
    if (uncategorized.length > 0) {
      sections.unshift({ category: { id: null, name: 'ללא קטגוריה', order: -1 }, resources: uncategorized });
    }
    return sections;
  })();

  return (
    <div className="page-wrapper">
      <Breadcrumbs
        items={[
          { to: '/disciplines', label: 'תחומי דעת' },
          { to: '/toshba/classes', label: 'תושב"ע' },
          { to: '/toshba/how-to-teach', label: 'הכנה למבחן מפמ"ר' },
          { label: topicLabel },
        ]}
      />

      <div className="toolbar-center">
        <h2 className="text-2xl md:text-3xl font-bold text-secondary-900 dark:text-white">
          הכנה למבחן מפמ"ר - {topicLabel}
        </h2>
      </div>

      <p className="text-center mt-2 mb-6">
        <Link
          to="/toshba/how-to-teach"
          className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
        >
          חזרה להכנה למבחן מפמ"ר
        </Link>
      </p>

      {loading && <SkeletonCardGrid count={4} />}

      {error && (
        <div className="text-center py-8">
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {!loading && !error && resourcesByCategory.map(({ category, resources: sectionResources }) => (
          <section key={category.id ?? 'uncategorized'} className="mt-8 first:mt-0">
            <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
              {category.name}
            </h3>
            <div className="grid-autofit">
              {sectionResources.map((resource) => {
                const hasFile = !!resource.file;
                const isExternal = !!resource.url && !hasFile;
                const downloadUrl = hasFile
                  ? `/api/how-to-teach-toshba-topic-resources/${resource.id}/download/`
                  : resource.url || resource.file;

                const handleClick = () => {
                  if (!resource.use_detail_modal && downloadUrl) {
                    window.open(
                      downloadUrl,
                      isExternal ? '_blank' : '_self',
                      isExternal ? 'noopener,noreferrer' : undefined
                    );
                    return;
                  }
                  const subjectLabel =
                    topicType === 'MISHNA' ? 'תושב״ע - משנה'
                    : topicType === 'GMARA' ? 'תושב״ע - גמרא'
                    : topicType === 'HALACHA' ? 'תושב״ע - הלכה'
                    : 'תושב״ע';
                  const modalItem = {
                    id: resource.id,
                    model_type: 'how_to_teach_toshba_resource',
                    title: resource.title || 'משאב ללא כותרת',
                    summary: resource.explanation || '',
                    explanation: resource.explanation || '',
                    author: resource.author || '',
                    subjectLabel: resource.discipline_name || subjectLabel,
                    typeLabel: resource.resource_type_label || 'משאב',
                    grades: resource.grades || [],
                    tags: [],
                    downloadUrl,
                    date: resource.created_at || '',
                  };
                  openResourceDetail(modalItem);
                };

                return (
                  <article
                    key={resource.id}
                    className="group bg-white dark:bg-secondary-800 rounded-2xl shadow-card overflow-hidden transition-all duration-300 hover:shadow-card-hover hover:-translate-y-1"
                  >
                    <button
                      type="button"
                      onClick={handleClick}
                      className="block w-full relative overflow-hidden bg-secondary-100 dark:bg-secondary-700"
                      style={{ aspectRatio: '3/2' }}
                    >
                      {resource.image ? (
                        <img
                          src={resource.image}
                          alt={resource.title}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <svg className="w-12 h-12 text-secondary-300 dark:text-secondary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                        </div>
                      )}
                    </button>
                    <div className="p-4 md:p-5">
                      <h3 className="text-lg font-semibold text-secondary-900 dark:text-white mb-1 text-center">
                        <button
                          type="button"
                          onClick={handleClick}
                          className="hover:text-primary-600 dark:hover:text-primary-400 transition-colors text-right w-full text-inherit"
                        >
                          {resource.title}
                        </button>
                      </h3>
                      {resource.author && (
                        <p className="text-sm text-secondary-600 dark:text-secondary-300 mb-2">
                          {resource.author}
                        </p>
                      )}
                      <p className="text-sm text-secondary-500 dark:text-secondary-400">
                        {isExternal ? 'קישור חיצוני' : 'לחצו להורדה'}
                      </p>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
      ))}

      {!loading && !error && resources.length === 0 && (
        <p className="text-center text-secondary-500 dark:text-secondary-400 py-8">
          אין משאבים להצגה.
        </p>
      )}
    </div>
  );
}
